// store/slices/adminSlice.ts - FIXED VERSION
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  AdminState,
  AdminConfiguration,
  User,
  Notification,
} from "../../types";
import adminService from "../../services/adminService";
import notificationService from "../../services/notificationService";

const initialState: AdminState = {
  configurations: [],
  users: [],
  selectedUser: null,
  userMetrics: null,
  notifications: [],
  notificationStatistics: null,
  isLoading: false,
  error: null,
};

// Fetch configurations
export const fetchConfigurations = createAsyncThunk(
  "admin/fetchConfigurations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getConfigurations();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch configurations",
      );
    }
  },
);

// Update configuration
export const updateConfiguration = createAsyncThunk(
  "admin/updateConfiguration",
  async ({ key, data }: { key: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateConfiguration(key, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to update configuration",
      );
    }
  },
);

// Fetch users - FIXED: Remove optional parameter or make it required
export const fetchUsers = createAsyncThunk(
  "admin/fetchUsers",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await adminService.getUsers(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch users",
      );
    }
  },
);

// Fetch user metrics
export const fetchUserMetrics = createAsyncThunk(
  "admin/fetchUserMetrics",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await adminService.getUserMetrics(userId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch user metrics",
      );
    }
  },
);

// Generate historical data
export const generateHistoricalData = createAsyncThunk(
  "admin/generateHistoricalData",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await adminService.generateHistoricalData(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to generate historical data",
      );
    }
  },
);

// Fetch notifications - FIXED: Remove optional parameter or make it required
export const fetchNotifications = createAsyncThunk(
  "admin/fetchNotifications",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await notificationService.getAdminNotifications(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch notifications",
      );
    }
  },
);

// Create notification
export const createNotification = createAsyncThunk(
  "admin/createNotification",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await notificationService.createNotification(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to create notification",
      );
    }
  },
);

// Update notification
export const updateNotification = createAsyncThunk(
  "admin/updateNotification",
  async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await notificationService.updateNotification(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to update notification",
      );
    }
  },
);

// Delete notification
export const deleteNotification = createAsyncThunk(
  "admin/deleteNotification",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await notificationService.deleteNotification(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete notification",
      );
    }
  },
);

// Fetch notification statistics
export const fetchNotificationStatistics = createAsyncThunk(
  "admin/fetchNotificationStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationService.getAdminNotificationStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error ||
          "Failed to fetch notification statistics",
      );
    }
  },
);

// Create user
export const createUser = createAsyncThunk(
  "admin/createUser",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await adminService.createUser(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to create user",
      );
    }
  },
);

// Update user
export const updateUser = createAsyncThunk(
  "admin/updateUser",
  async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateUser(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to update user",
      );
    }
  },
);

// Delete user
export const deleteUser = createAsyncThunk(
  "admin/deleteUser",
  async (id: number, { rejectWithValue }) => {
    try {
      await adminService.deleteUser(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete user",
      );
    }
  },
);

// Toggle user role
export const toggleUserRole = createAsyncThunk(
  "admin/toggleUserRole",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await adminService.toggleUserRole(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to toggle user role",
      );
    }
  },
);

// Update user balance
export const updateUserBalance = createAsyncThunk(
  "admin/updateUserBalance",
  async (data: { id: number; balance: number }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateUserBalance(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to update user balance",
      );
    }
  },
);

// Send email to a user
export const sendUserEmail = createAsyncThunk(
  "admin/sendUserEmail",
  async (
    { id, subject, message }: { id: number; subject: string; message: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await adminService.sendUserEmail(id, {
        subject,
        message,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.errors ||
          "Failed to send email",
      );
    }
  },
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    clearUserMetrics: (state) => {
      state.userMetrics = null;
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    clearNotificationStatistics: (state) => {
      state.notificationStatistics = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Configurations
      .addCase(fetchConfigurations.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchConfigurations.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          if (Array.isArray(action.payload?.data)) {
            state.configurations = action.payload.data;
          } else if (Array.isArray(action.payload)) {
            state.configurations = action.payload;
          } else {
            state.configurations = [];
          }
        },
      )
      .addCase(fetchConfigurations.rejected, (state) => {
        state.isLoading = false;
        state.configurations = [];
      })

      // Update Configuration
      .addCase(
        updateConfiguration.fulfilled,
        (state, action: PayloadAction<any>) => {
          const configData = action.payload?.data || action.payload;
          if (configData && configData.config_key) {
            const index = state.configurations.findIndex(
              (c: AdminConfiguration) => c.config_key === configData.config_key,
            );
            if (index !== -1) {
              state.configurations[index] = configData;
            } else {
              state.configurations.push(configData);
            }
          }
        },
      )

      // Users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        let userData = [];

        if (action.payload?.data?.data) {
          userData = action.payload.data.data;
        } else if (Array.isArray(action.payload?.data)) {
          userData = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          userData = action.payload;
        }

        state.users = userData.map((user: any) => ({
          id: user.id || 0,
          name: user.name || "",
          email: user.email || "",
          role: user.role || "user",
          is_active: user.is_active !== false,
          balance: user.balance || user.preferences?.balance || 0,
          created_at: user.created_at || new Date().toISOString(),
          updated_at: user.updated_at || new Date().toISOString(),
        }));
      })
      .addCase(fetchUsers.rejected, (state) => {
        state.isLoading = false;
        state.users = [];
      })

      // User Metrics
      .addCase(fetchUserMetrics.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchUserMetrics.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.userMetrics = action.payload?.data || action.payload;
        },
      )
      .addCase(fetchUserMetrics.rejected, (state) => {
        state.isLoading = false;
        state.userMetrics = null;
      })

      // Notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchNotifications.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.notifications = action.payload?.data || action.payload || [];
        },
      )
      .addCase(fetchNotifications.rejected, (state) => {
        state.isLoading = false;
        state.notifications = [];
      })

      // Create Notification
      .addCase(
        createNotification.fulfilled,
        (state, action: PayloadAction<any>) => {
          const newNotification = action.payload?.data || action.payload;
          if (newNotification) {
            state.notifications.unshift(newNotification);
          }
        },
      )

      // Update Notification
      .addCase(
        updateNotification.fulfilled,
        (state, action: PayloadAction<any>) => {
          const updatedNotification = action.payload?.data || action.payload;
          if (updatedNotification) {
            const index = state.notifications.findIndex(
              (n: Notification) => n.id === updatedNotification.id,
            );
            if (index !== -1) {
              state.notifications[index] = updatedNotification;
            }
          }
        },
      )

      // Delete Notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.notifications = state.notifications.filter(
          (n: Notification) => n.id !== id,
        );
      })

      // Notification Statistics
      .addCase(fetchNotificationStatistics.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchNotificationStatistics.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.notificationStatistics = action.payload?.data || action.payload;
        },
      )
      .addCase(fetchNotificationStatistics.rejected, (state) => {
        state.isLoading = false;
        state.notificationStatistics = null;
      })

      // Create User
      .addCase(createUser.fulfilled, (state, action: PayloadAction<any>) => {
        const newUser = action.payload?.user || action.payload;
        if (newUser) {
          state.users.push(newUser);
        }
      })

      // Update User
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<any>) => {
        const data =
          action.payload?.user || action.payload?.data || action.payload;
        if (data) {
          const index = state.users.findIndex((u: User) => u.id === data.id);
          if (index !== -1) {
            state.users[index] = {
              ...state.users[index],
              ...data,
              is_active: data.is_active !== false,
            };
          }
        }
      })

      // Delete User
      .addCase(deleteUser.fulfilled, (state, action) => {
        const userId = action.payload;
        state.users = state.users.filter((u) => u.id !== userId);
        if (state.selectedUser?.id === userId) {
          state.selectedUser = null;
        }
      })

      // Toggle User Role
      .addCase(
        toggleUserRole.fulfilled,
        (state, action: PayloadAction<any>) => {
          const updatedUser = action.payload?.user;
          if (updatedUser) {
            const index = state.users.findIndex((u) => u.id === updatedUser.id);
            if (index !== -1) {
              state.users[index] = updatedUser;
            }
          }
        },
      );
  },
});

export const {
  setSelectedUser,
  clearUserMetrics,
  clearNotifications,
  clearNotificationStatistics,
} = adminSlice.actions;
export default adminSlice.reducer;
